/**
 * Coups de pouce — Attendu 1127 « Calculer une fréquence simple »
 * (automatisme 4e).
 *
 * frequence_* (saisie, QCM) et vf_tableau_vf : entrée PARTAGÉE — la fréquence
 * est la part dans le total (effectif ÷ total), on ramène le total à 100 pour
 * le pourcentage, on contrôle (entre 0 et 100 %, somme 100 %). L'exemple
 * (revue 2026-09-08) : instruments sur 20 élèves, guitare 7 → 7 sur 20 →
 * 35 sur 100 → 35 %, pièges 7 % et 70 %. SVG piloté par les flèches de la
 * sheet (data-etapes / data-caption).
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 */

// Tableau SVG : en-tête 3 catégories + Total, ligne Effectif
const COLS = ['Instrument', 'Guitare', 'Piano', 'Batterie', 'Total'];
const W = [92, 58, 52, 62, 48]; const X0 = 22; const XS = W.reduce((acc, w, i) => { acc.push(i === 0 ? X0 : acc[i - 1] + W[i - 1]); return acc; }, []);
const cell = (i, y, txt, fill, couleur, gras) => `<rect x="${XS[i]}" y="${y}" width="${W[i]}" height="22" fill="${fill}" stroke="#94A3B8" stroke-width="1"/>`
    + `<text x="${XS[i] + W[i] / 2}" y="${y + 15}" font-size="11" font-weight="${gras ? 700 : 600}" fill="${couleur}" text-anchor="middle">${txt}</text>`;
const TABLEAU = COLS.map((c, i) => cell(i, 40, c, '#1E3A8A', '#ffffff', true)).join('')
    + cell(0, 62, 'Effectif', '#1E3A8A', '#ffffff', true) + cell(1, 62, '7', '#EFF6FF', '#1E3A8A', true) + cell(2, 62, '9', '#ffffff', '#1e293b')
    + cell(3, 62, '4', '#ffffff', '#1e293b') + cell(4, 62, '20', '#EFF6FF', '#1E3A8A', true);

/** Fraction dessinée : numérateur, barre, dénominateur, centrés en x ; la barre est à y. */
const FRAC = (x, y, n, d, taille, couleur, largeur) => `<text x="${x}" y="${y - 3}" font-size="${taille}" font-weight="700" fill="${couleur}" text-anchor="middle">${n}</text>`
    + `<line x1="${x - largeur / 2}" y1="${y}" x2="${x + largeur / 2}" y2="${y}" stroke="${couleur}" stroke-width="1.4"/>`
    + `<text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="700" fill="${couleur}" text-anchor="middle">${d}</text>`;

const SVG_FREQ = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 312" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Calculer la fréquence de la guitare, en 3 étapes à faire défiler avec les flèches. Tableau : guitare 7, piano 9, batterie 4, total 20 élèves. Étape 1 : la guitare, 7 élèves sur 20, la fraction 7 sur 20. Étape 2 : pour le pourcentage on cherche combien sur 100, la fraction point d'interrogation sur 100 ; 7 sur 20 égale point d'interrogation sur 100 ; une flèche va de 20 à 100 avec fois 5, une flèche va de 7 au point d'interrogation avec fois 5 ; 7 fois 5 égale 35, donc 35 sur 100, soit 35 pour cent. Étape 3 : pièges : 7 pour cent, c'est l'effectif, pas la fréquence ; 70 pour cent, on a multiplié par 10 au lieu de 5. Réponse : la fréquence de la guitare est 35 pour cent, soit 7 élèves sur 20">
  <style>
    @keyframes cp1127-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1127-pop { opacity: 0; animation: cp1127-pop 0.5s ease forwards; }
  </style>
  <defs>
    <marker id="cp1127-fleche" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#1D9E75"/>
    </marker>
  </defs>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Quelle est la fréquence de la guitare ?</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">On ramène le total à 100</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On évite deux pièges</text>

  <!-- ── Le tableau et la part (étape 1) ─────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU}
    <text x="8" y="108" font-size="11.5" font-weight="700" fill="#1E3A8A">La guitare : 7 élèves sur 20 (</text>
    ${FRAC(192, 103, '7', '20', 9, '#1E3A8A', 14)}
    <text x="201" y="108" font-size="11.5" font-weight="700" fill="#1E3A8A">)</text>
    <text x="8" y="127" font-size="10" font-weight="600" fill="#475569">La fréquence, c'est cette part : 7 divisé par 20.</text>
  </g>

  <!-- ── Ramener à 100 (étape 2) ─────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="162" font-size="10.5" font-weight="600" fill="#1D9E75">Pour le pourcentage, on cherche « combien sur 100 » (</text>
      ${FRAC(282, 158, '?', '100', 9.5, '#1D9E75', 20)}
      <text x="295" y="162" font-size="10.5" font-weight="600" fill="#1D9E75">)</text>
      ${FRAC(60, 200, '7', '20', 13, '#1D9E75', 26)}
      <text x="96" y="205" font-size="14" font-weight="800" fill="#1D9E75" text-anchor="middle">=</text>
      ${FRAC(136, 200, '?', '100', 13, '#1D9E75', 30)}
      <path d="M 70 185 Q 98 173 126 185" fill="none" stroke="#1D9E75" stroke-width="1.8" marker-end="url(#cp1127-fleche)"/>
      <text x="98" y="178" font-size="11" font-weight="800" fill="#DC2626" text-anchor="middle">× 5</text>
      <path d="M 70 218 Q 98 232 126 218" fill="none" stroke="#1D9E75" stroke-width="1.8" marker-end="url(#cp1127-fleche)"/>
      <text x="98" y="237" font-size="11" font-weight="800" fill="#DC2626" text-anchor="middle">× 5</text>
      <text x="184" y="188" font-size="11.5" font-weight="700" fill="#1D9E75">20 <tspan fill="#DC2626">× 5</tspan> = 100</text>
      <text x="184" y="206" font-size="11.5" font-weight="700" fill="#1D9E75">donc 7 <tspan fill="#DC2626">× 5</tspan> = 35</text>
      <text x="184" y="226" font-size="12" font-weight="800" fill="#1D9E75">35 sur 100 = 35 % ✓</text>
    </g>
  </g>

  <!-- ── Les pièges (étape 3) ────────────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="262" font-size="11.5" font-weight="700" fill="#B45309">7 % ✗  c'est l'effectif, pas la fréquence</text>
      <text x="8" y="278" font-size="11.5" font-weight="700" fill="#B45309">70 % ✗  on a multiplié par 10 au lieu de 5</text>
      <text x="8" y="300" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : la fréquence de la guitare est 35 %, soit 7 élèves sur 20.</text>
    </g>
  </g>

  <text x="180" y="308" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_FREQ = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**La fréquence d'une catégorie, c'est sa part dans le total** : effectif ÷ effectif total. Ce n'est pas l'effectif lui-même.",
                "**Pour l'écrire en pourcentage, ramène le total à 100** : sur 20, on multiplie effectif et total par 5 ; sur 25, par 4 ; sur 50, par 2 ; sur 10, par 10. Le nombre obtenu « sur 100 » est le pourcentage.",
                '**Vérifie** : une fréquence est toujours entre 0 % et 100 %, et les fréquences de toutes les catégories font 100 % ensemble.',
            ],
        },
        { titre: 'Un exemple', svg: SVG_FREQ },
    ],
};

// ─── Les trois écritures : la batterie, 4 sur 20 → 20 sur 100 → 20 % → 0,2 ───
const TABLEAU_B = COLS.map((c, i) => cell(i, 40, c, '#1E3A8A', '#ffffff', true)).join('')
    + cell(0, 62, 'Effectif', '#1E3A8A', '#ffffff', true) + cell(1, 62, '7', '#ffffff', '#1e293b') + cell(2, 62, '9', '#ffffff', '#1e293b')
    + cell(3, 62, '4', '#EFF6FF', '#1E3A8A', true) + cell(4, 62, '20', '#EFF6FF', '#1E3A8A', true);
const BOITE = (x, y, w, txt, couleur) => `<rect x="${x}" y="${y}" width="${w}" height="26" fill="none" stroke="${couleur}" stroke-width="2" rx="6"/>`
    + `<text x="${x + w / 2}" y="${y + 18}" font-size="12.5" font-weight="800" fill="${couleur}" text-anchor="middle">${txt}</text>`;

const SVG_ECRITURES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 340" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Les trois écritures de la fréquence de la batterie, en 3 étapes à faire défiler avec les flèches. Tableau : guitare 7, piano 9, batterie 4, total 20. Étape 1 : question, comment s'écrit la fréquence de la batterie ? La batterie, 4 élèves sur 20 ; en fraction, effectif sur total, soit 4 sur 20. Étape 2 : en pourcentage, on cherche combien sur 100 ; 4 sur 20 égale point d'interrogation sur 100 ; une flèche va de 20 à 100 avec fois 5, une flèche va de 4 au point d'interrogation avec fois 5 ; 4 fois 5 égale 20, donc 20 sur 100, soit 20 pour cent. Étape 3 : en écriture décimale, 20 pour cent c'est 20 centièmes, soit 0,20 égale 0,2 ; piège : 0,02 est faux, ce serait 2 pour cent. Réponse : la fréquence de la batterie s'écrit 4 sur 20, 20 pour cent ou 0,2">
  <style>
    @keyframes cp1127-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1127-pop { opacity: 0; animation: cp1127-pop 0.5s ease forwards; }
  </style>
  <defs>
    <marker id="cp1127-fleche-v" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#1D9E75"/>
    </marker>
  </defs>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">La fréquence de la batterie, en fraction</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Sur 100 : le pourcentage</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">En centièmes : l'écriture décimale</text>

  <!-- ── La fraction (étape 1) ───────────────────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU_B}
    <text x="8" y="103" font-size="11" font-weight="700" fill="#1e293b">Question : comment s'écrit la fréquence de la batterie ?</text>
    <text x="8" y="123" font-size="11.5" font-weight="700" fill="#1E3A8A">La batterie : 4 élèves sur 20</text>
    <text x="8" y="139" font-size="10" font-weight="600" fill="#475569">En fraction, la fréquence s'écrit effectif sur total, soit</text>
    ${FRAC(290, 135, '4', '20', 9, '#1E3A8A', 14)}
  </g>

  <!-- ── Le pourcentage (étape 2) ────────────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="168" font-size="10.5" font-weight="600" fill="#1D9E75">En pourcentage, on cherche « combien sur 100 » (</text>
      ${FRAC(275, 164, '?', '100', 9.5, '#1D9E75', 20)}
      <text x="288" y="168" font-size="10.5" font-weight="600" fill="#1D9E75">)</text>
      ${FRAC(60, 210, '4', '20', 13, '#1D9E75', 26)}
      <text x="96" y="215" font-size="14" font-weight="800" fill="#1D9E75" text-anchor="middle">=</text>
      ${FRAC(136, 210, '?', '100', 13, '#1D9E75', 30)}
      <path d="M 70 195 Q 98 183 126 195" fill="none" stroke="#1D9E75" stroke-width="1.8" marker-end="url(#cp1127-fleche-v)"/>
      <text x="98" y="188" font-size="11" font-weight="800" fill="#DC2626" text-anchor="middle">× 5</text>
      <path d="M 70 228 Q 98 242 126 228" fill="none" stroke="#1D9E75" stroke-width="1.8" marker-end="url(#cp1127-fleche-v)"/>
      <text x="98" y="247" font-size="11" font-weight="800" fill="#DC2626" text-anchor="middle">× 5</text>
      <text x="184" y="198" font-size="11.5" font-weight="700" fill="#1D9E75">20 <tspan fill="#DC2626">× 5</tspan> = 100</text>
      <text x="184" y="216" font-size="11.5" font-weight="700" fill="#1D9E75">donc 4 <tspan fill="#DC2626">× 5</tspan> = 20</text>
      <text x="184" y="236" font-size="12" font-weight="800" fill="#1D9E75">20 sur 100 = 20 % ✓</text>
    </g>
  </g>

  <!-- ── L'écriture décimale (étape 3) ───────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="272" font-size="11.5" font-weight="700" fill="#B45309">En décimal : 20 % = 20 centièmes = 0,20 = 0,2 ✓</text>
      <text x="8" y="288" font-size="11.5" font-weight="700" fill="#B45309">0,02 ✗  ce serait 2 centièmes, soit 2 %</text>
      <text x="8" y="312" font-size="11.5" font-weight="700" fill="#1e293b">Réponse : la fréquence de la batterie s'écrit 4 sur 20,</text>
      <text x="8" y="328" font-size="11.5" font-weight="700" fill="#1e293b">20 % ou 0,2 : trois écritures du même nombre.</text>
    </g>
  </g>

  <text x="180" y="336" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_ECRITURES = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**Une fréquence est un seul nombre qui s'écrit de trois façons** : une fraction (7 sur 20), un pourcentage (35 %), un nombre décimal (0,35).",
                "**De la fraction au pourcentage** : on ramène le total à 100. 7 sur 20, c'est 35 sur 100, donc 35 %.",
                "**Du pourcentage au décimal** : « 35 % » veut dire 35 centièmes, soit 0,35. Attention aux zéros : 5 % c'est 0,05 et non 0,5 ; 60 % c'est 0,6 et non 0,06.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_ECRITURES },
    ],
};

// ─── La fréquence manquante : 35 % / ? / 20 %, total 100 % → 45 % ─────────
const TABLEAU_F = COLS.map((c, i) => cell(i, 40, c, '#1E3A8A', '#ffffff', true)).join('')
    + cell(0, 62, 'Fréquence (%)', '#1E3A8A', '#ffffff', true) + cell(1, 62, '35', '#ffffff', '#1e293b') + cell(2, 62, '?', '#FFF7ED', '#B45309', true)
    + cell(3, 62, '20', '#ffffff', '#1e293b') + cell(4, 62, '100', '#ffffff', '#1e293b', true);

const SVG_MANQUANTE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 250" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Retrouver une fréquence effacée, en 3 étapes à faire défiler avec les flèches. Ligne des fréquences en pour cent : guitare 35, piano effacé, batterie 20, total 100. Étape 1 : les fréquences visibles font 35 plus 20 égale 55 pour cent. Étape 2 : la fréquence manquante est 100 moins 55 égale 45 pour cent. Étape 3 : contrôle, 35 plus 45 plus 20 égale 100, toutes les fréquences remises ensemble font 100 pour cent">
  <style>
    @keyframes cp1127-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1127-pop { opacity: 0; animation: cp1127-pop 0.5s ease forwards; }
  </style>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">Une fréquence a été effacée : le piano</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">Toutes les fréquences font 100 % ensemble</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">On contrôle, et on évite le piège</text>

  <!-- ── La ligne et la somme des visibles (étape 1) ─────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${TABLEAU_F}
    <text x="8" y="106" font-size="11.5" font-weight="700" fill="#1E3A8A">Les fréquences visibles : 35 + 20 = 55 %</text>
    <text x="8" y="121" font-size="10" font-weight="600" fill="#475569">chaque personne est comptée dans une catégorie et une seule</text>
  </g>

  <!-- ── La soustraction à 100 (étape 2) ─────────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="150" font-size="11.5" font-weight="700" fill="#1D9E75">Fréquence manquante = 100 − (somme des autres)</text>
      <text x="8" y="166" font-size="12.5" font-weight="800" fill="#1D9E75">100 − 55 = 45 % ✓</text>
    </g>
  </g>

  <!-- ── Contrôle et piège (étape 3) ─────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1127-pop">
      <text x="8" y="194" font-size="11.5" font-weight="700" fill="#B45309">Contrôle : 35 + 45 + 20 = 100 ✓</text>
      <text x="8" y="212" font-size="10" font-weight="600" fill="#B45309">toutes les fréquences remises ensemble font 100 %</text>
      <text x="180" y="246" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">100 − (somme des autres), puis on contrôle.</text>
    </g>
  </g>

  <text x="180" y="246" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_MANQUANTE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                '**Les fréquences de toutes les catégories font 100 % ensemble** : chaque personne interrogée est comptée dans une catégorie et une seule.',
                '**Additionne les fréquences que tu vois**, puis retire cette somme à 100 : fréquence manquante = 100 − (somme des autres). Même geste que pour un effectif manquant, avec 100 à la place du total.',
                "**Vérifie** : le résultat doit être entre 0 % et 100 %, et en l'ajoutant aux autres tu retrouves 100 %.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_MANQUANTE },
    ],
};

const CP_1127 = {
    frequence_saisie: AIDE_FREQ,
    frequence_qcm_unique: AIDE_FREQ,
    ecritures_texte_trou: AIDE_ECRITURES,
    manquante_saisie: AIDE_MANQUANTE,
    vf_tableau_vf: AIDE_FREQ,
};

export default CP_1127;
